import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  CircularProgress,
  Alert,
  InputAdornment,
  Pagination,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Analytics,
  Euro,
  Add,
  TrendingUp,
  AccountBalance,
  People,
  Search,
  CloudUploadOutlined,
  CheckCircle,
  Cancel,
  Download,
  Visibility,
} from '@mui/icons-material';
import { useDebounce } from '../hooks/useDebounce';
import api from '../api';

interface Client {
  _id: string;
  name: string;
  trackingCode: string;
  consultingFee: number;
  status: string;
}

interface Payment {
  _id: string;
  clientId: Client;
  amount: number;
  paymentDate: string;
  description?: string;
  receipt?: string; // File path for payment receipt
  createdBy: string;
  createdAt: string;
}

interface PaymentSummary {
  consultingFee: number;
  totalPaid: number;
  remainingBalance: number;
}

interface DashboardData {
  overview: {
    totalClients: number;
    totalConsultingFees: number;
    totalPaidAmount: number;
    totalRemainingBalance: number;
    fullyPaidClients: number;
    partiallyPaidClients: number;
    unpaidClients: number;
  };
}

export default function FinancialDashboardPage() {
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [clients, setClients] = useState<Client[]>([]);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [clientPayments, setClientPayments] = useState<Payment[]>([]);
  const [paymentSummary, setPaymentSummary] = useState<PaymentSummary | null>(null);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [showClientPayments, setShowClientPayments] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [error, setError] = useState<string | null>(null);

  // Debounce search term to avoid excessive API calls
  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  // Payment form
  const [paymentAmount, setPaymentAmount] = useState<number>(0);
  const [paymentDate, setPaymentDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [paymentDescription, setPaymentDescription] = useState<string>('');
  const [paymentReceipt, setPaymentReceipt] = useState<File | null>(null);
  const [uploadedReceiptName, setUploadedReceiptName] = useState<string>('');
  const [viewingReceipt, setViewingReceipt] = useState<{ filename: string; url: string } | null>(null);

  const fetchDashboardData = async () => {
    try {
      const response = await api.get('/payments/analytics/dashboard');
      setDashboardData(response.data.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao carregar dados do dashboard');
    }
  };

  const fetchClients = async () => {
    try {
      setLoading(true);
      const response = await api.get('/clients', {
        params: {
          page,
          limit: 10,
          search: debouncedSearchTerm || undefined,
        }
      });
      setClients(response.data.data);
      setTotalPages(response.data.pagination.pages);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao carregar clientes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  // Reset page to 1 when search term changes
  useEffect(() => {
    setPage(1);
  }, [debouncedSearchTerm]);

  useEffect(() => {
    fetchClients();
  }, [page, debouncedSearchTerm]); // Use debounced search term

  const fetchClientPayments = async (clientId: string) => {
    try {
      const response = await api.get(`/payments/client/${clientId}`);
      setClientPayments(response.data.data.payments);
      setPaymentSummary(response.data.data.summary);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao carregar pagamentos do cliente');
    }
  };

  const handleViewClientPayments = async (client: Client) => {
    setSelectedClient(client);
    await fetchClientPayments(client._id);
    setShowClientPayments(true);
  };

  const handleAddPayment = () => {
    setPaymentAmount(0);
    setPaymentDate(new Date().toISOString().split('T')[0]);
    setPaymentDescription('');
    setPaymentReceipt(null);
    setUploadedReceiptName('');
    setShowPaymentDialog(true);
  };

  const submitPayment = async () => {
    try {
      if (!selectedClient) return;

      const formData = new FormData();
      formData.append('clientId', selectedClient._id);
      formData.append('amount', paymentAmount.toString());
      formData.append('paymentDate', paymentDate);
      if (paymentDescription) {
        formData.append('description', paymentDescription);
      }
      if (paymentReceipt) {
        formData.append('receipt', paymentReceipt);
      }

      await api.post('/payments', formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      // Refresh data
      await fetchClientPayments(selectedClient._id);
      await fetchDashboardData();
      setShowPaymentDialog(false);
      
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao adicionar pagamento');
    }
  };

  const handleDownloadReceipt = async (filename: string) => {
    try {
      const response = await api.get(`/payments/receipt/${filename}`, {
        responseType: 'blob'
      });
      
      // Create blob link to download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Extract original filename or use the stored filename
      const contentDisposition = response.headers['content-disposition'];
      let downloadFilename = filename;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          downloadFilename = filenameMatch[1];
        }
      }
      
      link.setAttribute('download', downloadFilename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      setError('Erro ao baixar comprovativo');
    }
  };

  const handleViewReceipt = async (filename: string) => {
    try {
      const response = await api.get(`/payments/receipt/${filename}`, {
        responseType: 'blob'
      });
      
      // Ensure the blob has the correct MIME type
      const ext = filename.toLowerCase().split('.').pop();
      let mimeType = response.data.type;
      
      if (ext === 'pdf' && !mimeType.includes('pdf')) {
        mimeType = 'application/pdf';
      }
      
      const blob = new Blob([response.data], { type: mimeType });
      const url = window.URL.createObjectURL(blob);
      setViewingReceipt({ filename, url });
    } catch (err: any) {
      setError('Erro ao visualizar comprovativo');
    }
  };

  const closeReceiptViewer = () => {
    if (viewingReceipt) {
      window.URL.revokeObjectURL(viewingReceipt.url);
      setViewingReceipt(null);
    }
  };

  if (loading && !dashboardData) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', background: (theme) => theme.palette.grey[100], py: 3 }}>
      <Container maxWidth="xl">
        <Stack spacing={3}>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Analytics sx={{ fontSize: 32, color: 'primary.main' }} />
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              Dashboard Financeiro
            </Typography>
          </Box>

          {/* Error Alert */}
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* Overview Cards */}
          {dashboardData && (
            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
              <Card sx={{ flex: 1, minWidth: 250 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <People sx={{ fontSize: 40, color: 'primary.main' }} />
                    <Box>
                      <Typography variant="h6">{dashboardData.overview.totalClients}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Total de Clientes
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>

              <Card sx={{ flex: 1, minWidth: 250 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Euro sx={{ fontSize: 40, color: 'success.main' }} />
                    <Box>
                      <Typography variant="h6">€{dashboardData.overview.totalPaidAmount}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Total Recebido
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>

              <Card sx={{ flex: 1, minWidth: 250 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <TrendingUp sx={{ fontSize: 40, color: 'warning.main' }} />
                    <Box>
                      <Typography variant="h6">€{dashboardData.overview.totalRemainingBalance}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Saldo Pendente
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>

              <Card sx={{ flex: 1, minWidth: 250 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <AccountBalance sx={{ fontSize: 40, color: 'info.main' }} />
                    <Box>
                      <Typography variant="h6">€{dashboardData.overview.totalConsultingFees}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Total Esperado
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Box>
          )}

          {/* Payment Status Summary */}
          {dashboardData && (
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Resumo de Status de Pagamentos
                </Typography>
                <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" color="success.main">
                      {dashboardData.overview.fullyPaidClients}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Totalmente Pagos
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" color="warning.main">
                      {dashboardData.overview.partiallyPaidClients}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Parcialmente Pagos
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" color="error.main">
                      {dashboardData.overview.unpaidClients}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Não Pagos
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          )}

          {/* Clients Table */}
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  Gestão de Pagamentos por Cliente
                </Typography>
                <TextField
                  placeholder="Buscar clientes..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  slotProps={{
                    input: {
                      startAdornment: <InputAdornment position="start"><Search /></InputAdornment>
                    }
                  }}
                  size="small"
                  sx={{ minWidth: 250 }}
                />
              </Box>

              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                      <TableCell sx={{ fontWeight: 600 }}>Código</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Nome</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Taxa</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Ações</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {clients.map((client) => (
                      <TableRow key={client._id} hover>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                            {client.trackingCode}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography sx={{ fontWeight: 500 }}>
                            {client.name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Euro sx={{ fontSize: 16 }} />
                            <Typography>{client.consultingFee}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={client.status}
                            color={
                              client.status === 'ATIVA' ? 'success' :
                              client.status === 'REJEITADO PELA EQUIPE' ? 'error' :
                              'warning'
                            }
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => handleViewClientPayments(client)}
                          >
                            Ver Pagamentos
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Pagination */}
              {totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={(_, newPage) => setPage(newPage)}
                    color="primary"
                  />
                </Box>
              )}
            </CardContent>
          </Card>
        </Stack>
      </Container>

      {/* Client Payments Dialog */}
      <Dialog
        open={showClientPayments}
        onClose={() => setShowClientPayments(false)}
        maxWidth="md"
        fullWidth
      >
        {selectedClient && (
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">
                  Pagamentos - {selectedClient.name}
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={handleAddPayment}
                  size="small"
                >
                  Adicionar Pagamento
                </Button>
              </Box>
            </DialogTitle>
            <DialogContent>
              {paymentSummary && (
                <Card variant="outlined" sx={{ mb: 2 }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 2 }}>Resumo Financeiro</Typography>
                    <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                      <Box sx={{ flex: 1, minWidth: 150 }}>
                        <Typography variant="body2" color="text.secondary">Taxa de Consultoria</Typography>
                        <Typography variant="h6">€{paymentSummary.consultingFee}</Typography>
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 150 }}>
                        <Typography variant="body2" color="text.secondary">Total Pago</Typography>
                        <Typography variant="h6" color="success.main">€{paymentSummary.totalPaid}</Typography>
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 150 }}>
                        <Typography variant="body2" color="text.secondary">Saldo Restante</Typography>
                        <Typography variant="h6" color="warning.main">€{paymentSummary.remainingBalance}</Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              )}

              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Data</TableCell>
                      <TableCell>Valor</TableCell>
                      <TableCell>Descrição</TableCell>
                      <TableCell>Comprovativo</TableCell>
                      <TableCell>Registrado por</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {clientPayments.map((payment) => (
                      <TableRow key={payment._id}>
                        <TableCell>
                          {new Date(payment.paymentDate).toLocaleDateString('pt-BR')}
                        </TableCell>
                        <TableCell>€{payment.amount}</TableCell>
                        <TableCell>{payment.description || '-'}</TableCell>
                        <TableCell>
                          {payment.receipt ? (
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              <Tooltip title="Visualizar comprovativo">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => handleViewReceipt(payment.receipt!)}
                                >
                                  <Visibility fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Baixar comprovativo">
                                <IconButton
                                  size="small"
                                  color="success"
                                  onClick={() => handleDownloadReceipt(payment.receipt!)}
                                >
                                  <Download fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          ) : (
                            <Typography variant="body2" color="text.secondary">-</Typography>
                          )}
                        </TableCell>
                        <TableCell>{payment.createdBy}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setShowClientPayments(false)}>
                Fechar
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Add Payment Dialog */}
      <Dialog
        open={showPaymentDialog}
        onClose={() => setShowPaymentDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Adicionar Pagamento</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <TextField
              label="Valor"
              type="number"
              value={paymentAmount}
              onChange={(e) => setPaymentAmount(Number(e.target.value))}
              slotProps={{
                input: {
                  startAdornment: <InputAdornment position="start">€</InputAdornment>
                }
              }}
              fullWidth
              required
            />
            <TextField
              label="Data do Pagamento"
              type="date"
              value={paymentDate}
              onChange={(e) => setPaymentDate(e.target.value)}
              slotProps={{
                inputLabel: { shrink: true }
              }}
              fullWidth
              required
            />
            <TextField
              label="Descrição (opcional)"
              value={paymentDescription}
              onChange={(e) => setPaymentDescription(e.target.value)}
              multiline
              rows={3}
              fullWidth
            />
            
            <Box>
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                Comprovativo de Pagamento (opcional)
              </Typography>
              <Button
                component="label"
                variant="outlined"
                startIcon={<CloudUploadOutlined />}
                fullWidth
                sx={{ justifyContent: 'center', py: 1.5 }}
              >
                Selecionar arquivo (PDF ou imagem)
                <input
                  type="file"
                  accept="image/*,application/pdf"
                  hidden
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      setPaymentReceipt(file);
                      setUploadedReceiptName(file.name);
                    }
                  }}
                />
              </Button>
              {uploadedReceiptName && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, p: 1, bgcolor: 'success.lighter', borderRadius: 1 }}>
                  <CheckCircle sx={{ color: 'success.main', fontSize: 20 }} />
                  <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 600, flexGrow: 1 }}>
                    {uploadedReceiptName}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => {
                      setPaymentReceipt(null);
                      setUploadedReceiptName('');
                    }}
                    sx={{ color: 'error.main' }}
                  >
                    <Cancel sx={{ fontSize: 18 }} />
                  </IconButton>
                </Box>
              )}
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPaymentDialog(false)}>
            Cancelar
          </Button>
          <Button
            onClick={submitPayment}
            variant="contained"
            disabled={paymentAmount <= 0}
          >
            Adicionar Pagamento
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Receipt Viewer Dialog */}
      <Dialog
        open={!!viewingReceipt}
        onClose={closeReceiptViewer}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              Comprovativo de Pagamento
            </Typography>
            <Button
              startIcon={<Download />}
              onClick={() => viewingReceipt && handleDownloadReceipt(viewingReceipt.filename)}
              size="small"
            >
              Baixar
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent>
          {viewingReceipt && (
            <Box sx={{ textAlign: 'center', mt: 1 }}>
              {viewingReceipt.filename.toLowerCase().endsWith('.pdf') ? (
                <Box sx={{ width: '100%', height: '600px', border: '1px solid #ddd', borderRadius: 1 }}>
                  <iframe
                    src={viewingReceipt.url}
                    width="100%"
                    height="100%"
                    style={{ border: 'none' }}
                    title="Comprovativo de Pagamento"
                    onError={(e) => {
                      console.error('Error loading PDF in iframe:', e);
                      setError('Erro ao carregar PDF. Tente fazer o download do arquivo.');
                    }}
                  />
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Se o PDF não carregar corretamente, clique em "Baixar" para abrir em seu visualizador de PDF.
                  </Typography>
                </Box>
              ) : (
                <img
                  src={viewingReceipt.url}
                  alt="Comprovativo de Pagamento"
                  style={{
                    maxWidth: '100%',
                    maxHeight: '600px',
                    objectFit: 'contain',
                    border: '1px solid #ddd',
                    borderRadius: '4px'
                  }}
                  onError={(e) => {
                    console.error('Error loading image:', e);
                    setError('Erro ao carregar imagem.');
                  }}
                />
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeReceiptViewer}>
            Fechar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
} 